import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
// Copyright   : 2019 semantics GmbH
// Date        : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
// Author      : Olli Powroznik, Dirk Rothe
// Worker      : $Author: d.rothe $
// Revision    : $Rev: 156393 $
// Purpose     : swiper implementations
// =============================================================================
// ---Third-party imports
import React from 'react';
// ---Framework imports
import log from 'uix/logging';
import { get, isDigit, mapItems, uniq, strip, dict } from 'ldash';
import { useSelector, useState, useResponsive } from 'uix/hooks';
import { Actions, Sel } from 'uix/registries';
import { hashObj } from 'uix/utils';
import { decodeAttribH5 } from 'uix/utils';
import { GenericTagHandler } from 'uix/wiki/utils';
// lazy loadable component
import { Swiper } from 'uix/chunks';
//~ import Swiper from 'react-id-swiper'
// ---Local imports
import { wcUrl, fitWebcacheImage, ViewerLink } from './utils';
import { S2WP_DATA as DATA } from './testdata';
import S from './swiper.scss';
import { useWidgetData } from './hooks';
// https://react-id-swiper.ashernguyen.site/example/default
const MODES = {
    FreeMode: {
        slidesPerView: 5,
        spaceBetween: 30,
        freeMode: true,
        pagination: {
            el: '.swiper-pagination',
            clickable: true
        }
    },
    Cube: {
        effect: 'cube',
        grabCursor: true,
        cubeEffect: {
            shadow: true,
            slideShadows: true,
            shadowOffset: 20,
            shadowScale: 0.94
        },
        pagination: {
            el: '.swiper-pagination'
        }
    },
    CoverflowEffect: {
        effect: 'coverflow',
        grabCursor: true,
        centeredSlides: true,
        slidesPerView: 'auto',
        coverflowEffect: {
            rotate: 50,
            stretch: 0,
            depth: 100,
            modifier: 1,
            slideShadows: true
        },
        pagination: {
            el: '.swiper-pagination'
        }
    },
    LoopModeInfinityLoop: {
        slidesPerView: 1,
        spaceBetween: 30,
        loop: true,
        pagination: {
            el: '.swiper-pagination',
            clickable: true
        },
        navigation: {
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev'
        }
    },
    LoopModeWithMultipleSlidesPerGroup: {
        slidesPerView: 3,
        spaceBetween: 20,
        slidesPerGroup: 3,
        loop: true,
        loopFillGroupWithBlank: true,
        pagination: {
            el: '.swiper-pagination',
            clickable: true
        },
        navigation: {
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev'
        }
    },
    MutipleSlidesPerView: {
        slidesPerView: 3,
        spaceBetween: 0,
        loop: true,
        navigation: {
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev'
        }
    }
};
export const TitleSwiper = ({ items = DATA, mode = 'MutipleSlidesPerView' })=>{
    let apiurl = useSelector(Sel.apiurl);
    let [respondMode, ref, elementWidth] = useResponsive(100);
    let params = MODES[mode];
    let SLIDE_WIDTH = 220;
    if (mode == 'MutipleSlidesPerView') {
        let slidesPerView = Math.floor(elementWidth / (SLIDE_WIDTH * 2));
        params = {
            ...params,
            slidesPerView
        };
    }
    let divs = items.map((d, i)=>{
        let { width: pageWidth, height: pageHeight, wcWidth } = fitWebcacheImage({
            aspectRatio: d.pageWidth / d.pageHeight,
            width: SLIDE_WIDTH,
            height: SLIDE_WIDTH
        });
        let imgUrl = `url(${wcUrl(apiurl, wcWidth, d.pageId)})`;
        let pageImg = {
            backgroundImage: imgUrl,
            width: pageWidth,
            height: pageHeight
        };
        return /*#__PURE__*/ _jsx("div", {
            className: S.slideContainer,
            children: /*#__PURE__*/ _jsx(ViewerLink, {
                id: d.pageId,
                children: /*#__PURE__*/ _jsxs("div", {
                    id: S.slide,
                    children: [
                        /*#__PURE__*/ _jsx("span", {
                            className: S.title,
                            children: d.title
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: S.page,
                            style: pageImg
                        })
                    ]
                })
            })
        }, i);
    });
    return /*#__PURE__*/ _jsx("div", {
        ref: ref,
        style: {
            width: '100%'
        },
        children: /*#__PURE__*/ _jsx(Swiper, {
            ...params,
            children: divs
        }, hashObj([
            params,
            items
        ]))
    });
};
const ResolvedTitleSwiper = ({ items, ids, ...props })=>{
    let otid2title = dict(items.map(({ id, title })=>[
            id,
            title
        ]));
    let otids = uniq([
        ...ids,
        ...items.map((item)=>item.id)
    ]);
    let resolvedData = useWidgetData(otids);
    let swiperData = otids.filter((otid)=>resolvedData[otid]).map((otid)=>{
        let d = resolvedData[otid];
        return {
            title: get(otid2title, d.otid, d.md_caption),
            pageId: d.otid,
            pageHeight: d.height,
            pageWidth: d.width
        };
    });
    return /*#__PURE__*/ _jsx(TitleSwiper, {
        items: swiperData,
        ...props
    });
};
export const SwiperHandler = GenericTagHandler('Swiper', ({ attrib, children })=>{
    let { ids = '', ...props } = attrib;
    ids = ids.split(',').map((e)=>strip(e)).filter((e)=>isDigit(e));
    props = decodeAttribH5(props);
    let items = children.filter((e)=>e.tag.toLowerCase() === 'item' && isDigit(e.attrib.id)).map((e)=>({
            id: e.attrib.id,
            title: e.attrib.title
        }));
    return /*#__PURE__*/ _jsx(ResolvedTitleSwiper, {
        items: items,
        ids: ids,
        ...props
    });
});
